import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DspCreativeAsset, MediaType, StrategyDspFunnel, VideoDurationRange } from "@front/m19-api-client";
import { AccountSelectionService, DspCreativeAssetsService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ModalService,
} from "@front/m19-ui";
import { readVideoDurationSeconds } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  MediaSelectionModalComponent,
  MediaSelectionModalData,
  MediaSelectionModalResult,
} from "@m19-board/shared/media/media-selection-modal/media-selection-modal.component";
import { MediaComponent } from "@m19-board/shared/media/media/media.component";
import { map, of, startWith, switchMap, take } from "rxjs";

@Component({
  selector: "app-dsp-creative",
  standalone: true,
  imports: [
    IButtonDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    TranslocoDirective,
    MediaComponent,
    IIconComponent,
    IAlertComponent,
  ],
  templateUrl: "./dsp-creative.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCreativeComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly dspCreativeAssetsService = inject(DspCreativeAssetsService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly NOT_FOUND = "NOT_FOUND";

  readonly assetId = input.required<string>();
  readonly addAssetLabel = input.required<string>();
  readonly mediaType = input.required<MediaType>();
  readonly funnel = input<StrategyDspFunnel>();
  readonly allowDelete = input<boolean>(true);
  readonly isLoading = input<boolean>(false);
  readonly displayAssetName = input(true);
  readonly allowedVideoDurations = input<VideoDurationRange[]>([]);
  readonly framed = input<boolean>(false);

  readonly selectedAssetId = output<string>();
  readonly deleteAsset = output<void>();
  readonly assetToUpload = output<File>();
  readonly videoDurationSeconds = output<number | undefined>();

  protected readonly MediaType = MediaType;

  readonly readOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  readonly asset$ = toObservable(this.assetId).pipe(
    switchMap((assetId) => {
      if (!assetId) {
        return of(undefined);
      }
      return this.dspCreativeAssetsService.getAssetById(assetId).pipe(
        map((asset) => {
          if (asset) {
            return asset;
          }
          return {
            assetId: DspCreativeComponent.NOT_FOUND,
            url: "",
            mediaType: this.mediaType(),
            name: "",
            width: 0,
            height: 0,
          };
        }),
      );
    }),
  );
  readonly asset = toSignal(this.asset$);
  readonly durationSeconds$ = this.asset$.pipe(
    switchMap((asset) => {
      if (!asset || asset.assetId === DspCreativeComponent.NOT_FOUND || asset.mediaType !== MediaType.videoAsset) {
        return of(undefined);
      }
      return readVideoDurationSeconds(asset.url);
    }),
  );
  readonly durationSeconds = toSignal(this.durationSeconds$);

  readonly dspCreativeAssets = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        this.dspCreativeAssetsService.getDspCreativeAssets(am.accountId, am.marketplace, am.dspAdvertiserId!),
      ),
      startWith([]),
    ),
    { requireSync: true },
  );
  readonly availableAssets = computed(() => {
    return this.dspCreativeAssets().filter(
      (a) => !this.funnel() || DspCreativeAssetsService.isVideoEligible(a, this.funnel()!),
    );
  });
  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);

  constructor() {
    this.durationSeconds$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((duration) => {
      this.videoDurationSeconds.emit(duration);
    });
  }

  selectMedia() {
    if (this.readOnly()) return;
    this.modalService
      .openModal<MediaSelectionModalData, MediaSelectionModalResult<DspCreativeAsset>>(MediaSelectionModalComponent, {
        modalTitle: this.translocoService.translate("media-input.select_a_file"),
        data: {
          medias: this.availableAssets(),
          mediaType: this.mediaType(),
          disableUpload: false,
          isDsp: true,
          allowedVideoDurations: this.allowedVideoDurations(),
          dspAdvertiserId: this.accountMarketplace()?.dspAdvertiserId,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result.newFile) {
          this.assetToUpload.emit(result.newFile);
        } else if (result.selectedMedia) {
          this.selectedAssetId.emit(result.selectedMedia?.assetId);
        }
      });
  }

  deleteMedia() {
    this.deleteAsset.emit();
  }
}
